import { useCallback, useEffect, useState } from 'react'

import AuthCard from 'src/features/auth/components/auth-card'
import AuthContent from 'src/features/auth/components/auth-content'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
import AuthTitle from 'src/features/auth/components/auth-title'
import { logger } from 'src/lib/logger'
import { cn } from 'src/lib/utils'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useRegisterUserInBackend } from 'src/shared/features/users/api/accounts/register-backend-user'
import CompletionStep from 'src/shared/features/users/components/onboarding/form-steps/completion-step'
import StepFirstName from 'src/shared/features/users/components/onboarding/form-steps/first-name-step'
import StepLastName from 'src/shared/features/users/components/onboarding/form-steps/last-name-step'
import StepProfession from 'src/shared/features/users/components/onboarding/form-steps/profession-step'
import StepUsername from 'src/shared/features/users/components/onboarding/form-steps/username-step'
import type { Language } from 'src/shared/features/users/schemas/language'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { onboardingSchema } from 'src/shared/features/users/schemas/user'
import { useToast } from 'src/shared/hooks/use-toast'
import { useEventStore } from 'src/shared/store/event-store'

const steps = [
  {
    name: 'FirstName',
    title: 'Enter your first name',
    subtitle: 'This will be displayed on your public profile.',
    component: StepFirstName,
  },
  {
    name: 'LastName',
    title: 'Enter your last name',
    subtitle: 'This will be displayed on your public profile.',
    component: StepLastName,
  },
  {
    name: 'Username',
    title: 'Choose your username',
    subtitle: 'Pick a username to represent you on Conrad.',
    additionalLabel: 'Special characters are not allowed, except for underscores (_) and periods (.)',
    component: StepUsername,
  },
  {
    name: 'Profession',
    title: "What's your profession?",
    subtitle: 'Select the one you primarily focus on!',
    component: StepProfession,
  },
  {
    name: 'Completion',
    component: CompletionStep,
  },
]

type OnboardingData = {
  firstName?: string
  lastName?: string
  username?: string
  streetAddress1?: string
  streetAddress2?: string
  city?: string
  state?: string
  zip?: string
  latitude?: number
  longitude?: number
  languages?: Language[]
  profession?: Profession
}

interface ProfessionalOnboardingFormProps {
  onSuccess: () => void
  setStep: (number: number) => void
}

export default function ProfessionalOnboardingForm({ onSuccess, setStep }: ProfessionalOnboardingFormProps) {
  const { currentAuth, isSignedIn, needsOnboarding } = useAuthState()
  const [currentStep, setCurrentStep] = useState(0)
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()
  const { mutateAsync: registerUser } = useRegisterUserInBackend()
  const clearFlowId = useEventStore((state) => state.clearFlowId)

  // Clear flow ID when component unmounts
  useEffect(() => {
    return () => {
      clearFlowId()
    }
  }, [clearFlowId])

  useEffect(() => {
    if (currentStep === steps.length - 1) {
      return
    }
  }, [isSignedIn, needsOnboarding, currentStep])

  const handleSubmit = useCallback(
    async (finalData: OnboardingData) => {
      if (isSubmitting) return

      setIsSubmitting(true)
      try {
        const validatedData = onboardingSchema.parse({
          ...finalData,
          primaryEmail: currentAuth?.email || '',
        })

        const response = await registerUser(validatedData)

        if (!response) {
          throw new Error('Failed to register user')
        }

        setCurrentStep((prevStep) => prevStep + 1)
        setStep(currentStep + 1)
        onSuccess()
      } catch (error) {
        logger.error('Onboarding submission error:', error)
        toast({
          title: 'Error',
          description: 'There was an error completing your registration. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [isSubmitting, setIsSubmitting, currentAuth, registerUser, setCurrentStep, toast, setStep, currentStep],
  )

  const handleNext = useCallback(
    async (stepData: Partial<OnboardingData>) => {
      const prevData = onboardingData
      const updatedData = { ...prevData, ...stepData }

      if (currentStep < steps.length - 2) {
        // Move to next step if not at profession step yet
        setCurrentStep((prevStep) => prevStep + 1)
        setStep(currentStep + 1)
        setOnboardingData(updatedData)
      } else {
        // Submit when we reach the profession step (last step before completion)
        await handleSubmit(updatedData)
        setOnboardingData(updatedData)
      }
    },
    [currentStep, handleSubmit, setStep, onboardingData],
  )

  // Using useCallback to prevent unnecessary re-renders
  const renderStepComponent = useCallback(() => {
    const CurrentStepComponent = steps[currentStep].component
    return (
      <CurrentStepComponent
        key={`step-${currentStep}`}
        onNext={handleNext}
        initialData={onboardingData}
        additionalLabel={steps[currentStep].additionalLabel}
        variant="two-column"
      />
    )
  }, [currentStep, handleNext, onboardingData])

  return (
    <div className="">
      {currentStep < steps.length && (
        <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
          <AuthContent className="max-w-none">
            <AuthTitle title={steps[currentStep].title || ''} className="text-[32px] tracking-[-0.32px]" />
            <AuthSubtitle
              className={cn(currentStep === steps.length - 2 && 'text-base')}
              description={steps[currentStep].subtitle || ''}
            />
            <div className="mt-6 grow">{renderStepComponent()}</div>
          </AuthContent>
        </AuthCard>
      )}
    </div>
  )
}
